import baseAxios from "../core/api/axios/baseAxios";
import investmentBaseAxios from "../core/api/axios/investmentBaseAxios"
import newBaseAxios from "../core/api/axios/newBaseAxios";

class AppService {
  static async uploadFile(base64_data) {
    const fileData = new FormData();
    fileData.append("file", `data:image/png;base64,${base64_data}`);
    fileData.append("upload_preset", "live_report");
    const res = await baseAxios.post(
      "https://api.cloudinary.com/v1_1/dksyt7yvf/upload",
      fileData
    );
    return res?.data || res;
  }

  static async editCompanyProfile(data) {
    const res = await baseAxios.post("/api/admin/login", data);
    return res?.data || res;
  }

  static async getAllAdmins() {
    const res = await baseAxios.get("/api/get/all/admins");
    return res?.data || res;
  }
  static async GetCompanyReviews(data) {
    const res = await newBaseAxios.get("/v2/admin/company/reviews?start=2010-01-01&end=2025-01-01");
    return res?.data || res;
  }
  static async ReplyReviews(data) {
    const res = await newBaseAxios.post("/v2/admin/company/review/reply", data);
    return res?.data || res;
  }
  static async exportCSV(data) {
    try {
      const exportOptions = typeof data === "string" ? { type: data } : (data || {});
      if ((exportOptions.type || exportOptions.endpoint) && Array.isArray(exportOptions.columns)) {
        const limit = 500;
        const readPage = async (page) => {
          const endpointFilters = exportOptions.endpoint
            ? {
                ...(exportOptions.query || {}),
                ...(exportOptions.filters?.searchTerm ? { key: exportOptions.filters.searchTerm } : {}),
                ...(exportOptions.filters?.key ? { key: exportOptions.filters.key } : {}),
                ...(exportOptions.filters?.start || exportOptions.filters?.startDate
                  ? { start: exportOptions.filters.start || exportOptions.filters.startDate }
                  : {}),
                ...(exportOptions.filters?.end || exportOptions.filters?.endDate
                  ? { end: exportOptions.filters.end || exportOptions.filters.endDate }
                  : {}),
              }
            : null;
          const params = new URLSearchParams(exportOptions.endpoint
            ? {
                ...endpointFilters,
                page: String(page),
                resultsPerPage: String(limit),
              }
            : {
                type: exportOptions.type,
                page: String(page),
                limit: String(limit),
                ...(exportOptions.filters || {}),
              });
          const client = exportOptions.api === "primary" ? baseAxios : newBaseAxios;
          const response = await client.get(`${exportOptions.endpoint || "/v2/dashboard/details"}?${params.toString()}`);
          const result = response.data?.data || response.data;
          return {
            rows: result?.[exportOptions.rowsKey] || result?.data || result?.users || [],
            totalPages: Number(result?.totalPages) || 1,
            totalResult: Number(result?.totalResult ?? result?.total ?? result?.totalUsers) || 0,
          };
        };

        const firstPage = await readPage(1);
        const expectedTotal = Number(exportOptions.expectedTotal) || firstPage.totalResult;
        const pagesToRead = expectedTotal
          ? Math.ceil(expectedTotal / limit)
          : firstPage.totalPages;
        const pages = [];
        for (let start = 2; start <= pagesToRead; start += 4) {
          const pageNumbers = Array.from(
            { length: Math.min(4, pagesToRead - start + 1) },
            (_, index) => start + index
          );
          pages.push(...await Promise.all(pageNumbers.map(readPage)));
        }
        const rows = [...firstPage.rows, ...pages.flatMap((page) => page.rows)];
        if (expectedTotal && rows.length < expectedTotal) {
          throw new Error(`The server returned ${rows.length.toLocaleString()} of ${expectedTotal.toLocaleString()} records. No partial CSV was downloaded; please retry or contact support.`);
        }
        const fields = exportOptions.columns || [];
        if (!fields.length) throw new Error("No exportable columns are configured for this list.");

        const readValue = (record, dataIndex) => {
          const path = Array.isArray(dataIndex) ? dataIndex : String(dataIndex).split(".");
          const value = path.reduce((current, key) => current?.[key], record);
          return value && typeof value === "object" && value.title ? value.title : value;
        };
        const cell = (value) => {
          let text = value == null ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
          if (/^[=+@\-\t\r]/.test(text)) text = `'${text}`;
          return `"${text.replace(/"/g, '""')}"`;
        };
        const csvRows = [
          fields.map((field) => cell(field.title)).join(","),
          ...rows.map((record) => fields.map((field) => cell(readValue(record, field.dataIndex))).join(",")),
        ];
        if (!rows.length) csvRows.push('"No records found"');
        const blob = new Blob([`\uFEFF${csvRows.join("\r\n")}`], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = exportOptions.filename || `${(exportOptions.title || "users").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        return true;
      }

      const params = new URLSearchParams({
        type: exportOptions.type || "",
        isDownloadable: "true",
        ...(exportOptions.filters || {}),
      });
      const res = await newBaseAxios.get(
        `/v2/dashboard/details?${params.toString()}`,
        { responseType: "blob" }
      );

      const blob = res.data instanceof Blob
        ? res.data
        : new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const contentType = blob.type || res.headers?.["content-type"] || "";
      if (contentType.includes("json") || contentType.includes("text/html")) {
        const errorText = await blob.text();
        let message = errorText || "The server could not create the export.";
        try {
          const parsed = JSON.parse(errorText);
          message = parsed.message?.message || parsed.message || message;
        } catch (_) {
          // Keep the plain-text server error for the alert below.
        }
        throw new Error(typeof message === "string" ? message : "The server could not create the export.");
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers?.["content-disposition"] || "";
      const filename = disposition.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i)?.[1];
      link.download = exportOptions.filename || (filename ? decodeURIComponent(filename.replace(/"/g, "")) : `${exportOptions.type || "export"}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      return true;
    } catch (error) {
      console.error('Error downloading the file:', error);
      throw error;
    }
  }
  
}

export default AppService;
